import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import toast from 'react-hot-toast';
import {
  Ban,
  Copy,
  Plus,
  RotateCw,
  ShieldAlert,
  Smartphone,
  Tablet,
} from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import {
  createDevicePairCode,
  createWaiterPairCode,
  disableWaitersModule,
  enableWaitersModule,
  getWaitersModule,
  listDevices,
  listUsers,
  revokeDevice,
  type DeviceRole,
  type DeviceRow,
  type PairCodeResponse,
} from '@/services/admin';
import { useEstablishmentStore } from '@/store/establishment.store';
import { formatDate } from '@/utils/format';
import {
  DisableWaitersModal,
  EnableWaitersModal,
  WaitersModuleBar,
  WaitersModulePromo,
} from './devices/WaitersModule';

type Tab = 'kassa' | 'waiters';
type WaiterTarget = { waiterId: string } | { name: string };

// ──────────────────────────────────────────────────────────────
// Утилиты отображения устройства в списке.

function onlineBadge(d: DeviceRow) {
  if (d.revoked) return <Badge tone="danger">Отозвано</Badge>;
  if (d.online) return <Badge tone="success">В сети</Badge>;
  return <Badge tone="slate">Оффлайн</Badge>;
}

// Телефон официанта отключается и вместе с его учёткой: заблокированного в
// «Пользователях» видно прямо здесь, а не только по пропавшим заказам.
function waiterBadge(d: DeviceRow) {
  if (d.revoked) return <Badge tone="danger">Отключён</Badge>;
  if (d.waiter?.status === 'BLOCKED') return <Badge tone="warning">Заблокирован</Badge>;
  if (d.waiter?.status === 'DELETED' || !d.waiter) return <Badge tone="danger">Удалён</Badge>;
  if (d.online) return <Badge tone="success">В сети</Badge>;
  return <Badge tone="slate">Оффлайн</Badge>;
}

function waiterName(d: DeviceRow): string {
  return d.waiter?.name?.trim() || d.waiter?.email || 'Официант';
}

// Офлайн-очередь кассы (п.4): сколько операций ещё не ушло на сервер.
// Касса шлёт значение в heartbeat; null — старая версия без heartbeat.
function pendingBadge(d: DeviceRow) {
  if (d.revoked || d.pendingOps === null || d.pendingOps === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  if (d.pendingOps === 0) return <Badge tone="success">пусто</Badge>;
  // Застряла: операции есть, а касса не в сети — данные могут потеряться.
  const stuck = !d.online && d.pendingOps > 0;
  return (
    <Badge tone={stuck ? 'danger' : 'warning'}>
      {d.pendingOps} {stuck ? 'застряло' : 'в очереди'}
    </Badge>
  );
}

function shortAgent(ua: string | null): string {
  if (!ua) return '—';
  // Из User-Agent iOS-кассы обычно что-то вроде
  //   "Cashier/1.0 (iPad; iOS 17.2; ru_RU)"
  // Достаём часть в скобках.
  const m = ua.match(/\(([^)]+)\)/);
  return (m?.[1] ?? ua).slice(0, 60);
}

// ──────────────────────────────────────────────────────────────
// Модалка показа pair-кода с живым таймером и QR.
// После истечения — блок превращается в "код истёк" с кнопкой заново.

function PairCodeModal({
  data,
  variant,
  onClose,
  onRegenerate,
}: {
  data: PairCodeResponse;
  variant: Tab;
  onClose: () => void;
  onRegenerate: () => void;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(data.expiresInSeconds);

  useEffect(() => {
    // Приложение официанта QR не сканирует — для его кода картинку не рисуем.
    if (variant !== 'kassa') return;
    // В QR кладём JSON с кодом — приложению так удобнее парсить (и при
    // желании можно будет добавить поле `base` с базовым API-URL).
    // Для простоты: только `code`, потому что base у кассы уже
    // захардкожена в приложении (resstro.ru prod).
    const payload = JSON.stringify({ code: data.code, v: 1 });
    QRCode.toDataURL(payload, { margin: 1, scale: 8 })
      .then(setQr)
      .catch(() => setQr(null));
  }, [data.code, variant]);

  useEffect(() => {
    const endsAt = new Date(data.expiresAt).getTime();
    const tick = () => {
      const left = Math.max(0, Math.round((endsAt - Date.now()) / 1000));
      setRemaining(left);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [data.expiresAt]);

  const expired = remaining <= 0;
  const mm = Math.floor(remaining / 60).toString().padStart(2, '0');
  const ss = (remaining % 60).toString().padStart(2, '0');

  const copy = () => {
    navigator.clipboard.writeText(data.code).then(() => toast.success('Скопировано'));
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={variant === 'kassa' ? 'Подключить кассу' : `Подключить официанта: ${data.suggestedName}`}
    >
      <div className="flex flex-col items-center gap-5 py-2">
        {variant === 'kassa' ? (
          <p className="text-sm text-slate-600 text-center max-w-sm">
            На кассе откройте приложение <strong>Resstro Касса</strong>, нажмите
            <strong> «Подключить»</strong> и введите 6-значный код или отсканируйте QR.
          </p>
        ) : (
          <p className="text-sm text-slate-600 text-center max-w-sm">
            Официант открывает на телефоне приложение <strong>Resstro Официант</strong> и
            вводит этот код. Скачать приложение — на{' '}
            <a
              className="text-slate-900 underline underline-offset-2"
              href="https://resstro.ru/downloads"
              target="_blank"
              rel="noopener noreferrer"
            >
              resstro.ru/downloads
            </a>
            .
          </p>
        )}

        <div className="flex items-center gap-3">
          {data.code.split('').map((ch, i) => (
            <div
              key={i}
              className={`w-12 h-14 flex items-center justify-center rounded-lg text-2xl font-semibold tabular-nums border ${
                expired
                  ? 'border-slate-200 bg-slate-50 text-slate-300'
                  : 'border-slate-200 bg-white text-slate-900 shadow-sm'
              }`}
            >
              {ch}
            </div>
          ))}
        </div>

        {qr && !expired && (
          <img
            src={qr}
            alt="QR код"
            className="w-44 h-44 rounded-lg border border-slate-100"
          />
        )}

        {!expired ? (
          <>
            <div className="text-sm text-slate-500">
              Код действителен ещё <span className="tabular-nums">{mm}:{ss}</span>
            </div>
            <button className="btn-ghost" onClick={copy}>
              <Copy size={14} /> Скопировать код
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="text-sm text-rose-600">Код истёк</div>
            <button className="btn-primary" onClick={onRegenerate}>
              Сгенерировать новый
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────
// Для кого код официанта: уже заведённый официант (новый телефон, история
// продаж сохраняется) или новый — по имени, почта ему не нужна.

function WaiterPairModal({
  pending,
  onClose,
  onSubmit,
}: {
  pending: boolean;
  onClose: () => void;
  onSubmit: (target: WaiterTarget) => void;
}) {
  const { data } = useQuery({
    queryKey: ['admin/users', 'waiters-active'],
    queryFn: () => listUsers({ role: 'WAITER', status: 'ACTIVE' }),
  });
  const waiters = data?.results ?? [];
  const [choice, setChoice] = useState('new');
  const [name, setName] = useState('');
  const canSubmit = !pending && (choice !== 'new' || name.trim().length > 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(choice === 'new' ? { name: name.trim() } : { waiterId: choice });
  };

  return (
    <Modal open onClose={onClose} title="Подключить официанта" size="sm">
      <form className="grid gap-4" onSubmit={submit}>
        {waiters.length > 0 && (
          <div>
            <label className="label">Официант</label>
            <select className="input" value={choice} onChange={(e) => setChoice(e.target.value)}>
              <option value="new">Новый официант</option>
              {waiters.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name?.trim() || w.email}
                </option>
              ))}
            </select>
          </div>
        )}
        {choice === 'new' ? (
          <div>
            <label className="label">Имя официанта</label>
            <input
              className="input"
              autoFocus
              maxLength={50}
              placeholder="Анна"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-xs text-slate-400 mt-1">
              Появится в «Пользователях» с ролью «Официант», когда введёт код. Почта не нужна.
            </p>
          </div>
        ) : (
          <p className="text-xs text-slate-500">
            Телефон привяжется к этому официанту — его заказы и продажи останутся при нём.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={!canSubmit}>
            Получить код
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────

export default function DevicesPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() =>
    searchParams.get('tab') === 'waiters' ? 'waiters' : 'kassa',
  );
  const [pairCode, setPairCode] = useState<{ data: PairCodeResponse; variant: Tab } | null>(null);
  const [pickingWaiter, setPickingWaiter] = useState(false);
  const [waiterTarget, setWaiterTarget] = useState<WaiterTarget | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<DeviceRow | null>(null);
  const [moduleDialog, setModuleDialog] = useState<'enable' | 'disable' | null>(null);
  const revokeReasonRef = useRef<HTMLInputElement>(null);
  const estId = useEstablishmentStore((s) => s.currentId);

  const role: DeviceRole = tab === 'waiters' ? 'WAITER' : 'KASSA';
  const { data, isLoading } = useQuery({
    queryKey: ['admin/devices', role],
    queryFn: () => listDevices(role),
    // Пока открыта страница — обновляем список каждые 10 секунд,
    // чтобы было видно как устройство заходит в сеть.
    refetchInterval: 10_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin/devices'] });

  const showError = (err: unknown) => {
    const msg = err instanceof Error ? err.message : 'Не удалось сгенерировать код';
    toast.error(msg);
  };

  const pairMut = useMutation({
    mutationFn: (name?: string) => createDevicePairCode(name),
    onSuccess: (res) => setPairCode({ data: res, variant: 'kassa' }),
    onError: showError,
  });

  const waiterPairMut = useMutation({
    mutationFn: (target: WaiterTarget) => createWaiterPairCode(target),
    onSuccess: (res, target) => {
      setPickingWaiter(false);
      setWaiterTarget(target);
      setPairCode({ data: res, variant: 'waiters' });
    },
    onError: showError,
  });

  const revokeMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => revokeDevice(id, reason),
    onSuccess: () => {
      toast.success('Доступ отозван');
      setRevokeTarget(null);
      invalidate();
    },
  });

  // Модуль «Официанты» (500 ₽/мес): без него телефоны официантов не работают.
  const moduleKey = ['admin/modules/waiters', estId] as const;
  const moduleQuery = useQuery({
    queryKey: moduleKey,
    queryFn: getWaitersModule,
    enabled: tab === 'waiters',
    retry: false,
  });
  const waitersModule = tab === 'waiters' ? (moduleQuery.data ?? null) : null;
  const moduleOff = waitersModule?.state === 'off';
  const moduleError = (err: unknown) =>
    toast.error(err instanceof Error ? err.message : 'Не удалось изменить модуль');

  const enableMut = useMutation({
    mutationFn: () => enableWaitersModule(),
    onSuccess: (next) => {
      const firstTime = waitersModule?.state === 'off';
      qc.setQueryData(moduleKey, next);
      setModuleDialog(null);
      toast.success(firstTime ? 'Официанты подключены' : 'Модуль снова подключён');
      // Следующий шаг после подключения — телефон первого официанта.
      if (firstTime) setPickingWaiter(true);
    },
    onError: moduleError,
  });

  const disableMut = useMutation({
    mutationFn: () => disableWaitersModule(),
    onSuccess: (next) => {
      qc.setQueryData(moduleKey, next);
      setModuleDialog(null);
      toast.success(next.state === 'ending' ? 'Модуль доработает оплаченный период' : 'Модуль отключён');
    },
    onError: moduleError,
  });

  const rows = useMemo(() => data?.results ?? [], [data?.results]);
  const activeCount = rows.filter((r) => !r.revoked).length;
  // Код официанта выдаём, только когда модуль работает (или бэкенд о модуле не знает).
  const canPairWaiter = moduleQuery.isError || (!!waitersModule && waitersModule.state !== 'off');

  const switchTab = (next: Tab) => {
    setTab(next);
    setSearchParams(
      (prev) => {
        if (next === 'waiters') prev.set('tab', 'waiters');
        else prev.delete('tab');
        return prev;
      },
      { replace: true },
    );
  };

  // Deep-link из онбординга: /devices?pair=1 сразу генерирует код привязки.
  useEffect(() => {
    if (searchParams.get('pair') === '1') {
      pairMut.mutate(undefined);
      setSearchParams(
        (prev) => {
          prev.delete('pair');
          return prev;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Устройства</h1>
          <p className="text-sm text-slate-500">
            {tab === 'kassa' ? 'Кассы, привязанные к заведению.' : 'Телефоны официантов.'}{' '}
            Подключено: {activeCount}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={() => invalidate()} title="Обновить">
            <RotateCw size={14} />
          </button>
          {tab === 'kassa' ? (
            <button
              className="btn-primary shrink-0"
              onClick={() => pairMut.mutate(undefined)}
              disabled={pairMut.isPending}
            >
              <Plus size={16} /> Подключить кассу
            </button>
          ) : canPairWaiter ? (
            <button className="btn-primary shrink-0" onClick={() => setPickingWaiter(true)}>
              <Plus size={16} /> Подключить официанта
            </button>
          ) : null}
        </div>
      </header>

      <div className="mb-4 inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
        {([['kassa', 'Кассы'], ['waiters', 'Официанты']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => switchTab(id)}
            className={`px-3 py-1.5 ${tab === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {waitersModule && waitersModule.state !== 'off' && (
        <WaitersModuleBar
          status={waitersModule}
          pending={enableMut.isPending || disableMut.isPending}
          onEnable={() => setModuleDialog('enable')}
          onDisable={() => setModuleDialog('disable')}
        />
      )}

      {moduleOff && waitersModule && (
        <WaitersModulePromo
          status={waitersModule}
          pending={enableMut.isPending}
          onEnable={() => setModuleDialog('enable')}
        />
      )}

      {moduleOff && rows.length > 0 && (
        <div className="mt-8 mb-2">
          <h3 className="text-sm font-medium text-slate-800">Подключённые телефоны</h3>
          <p className="text-xs text-slate-500">Заработают сразу после подключения модуля.</p>
        </div>
      )}

      {(!moduleOff || rows.length > 0) && (
      <div className="card overflow-x-auto">
        {isLoading ? (
          <Spinner label="Загружаем устройства" />
        ) : rows.length === 0 ? (
          tab === 'kassa' ? (
            <div className="p-10 text-center text-sm text-slate-500">
              <Tablet size={24} className="mx-auto text-slate-300 mb-2" />
              Пока нет подключённых касс. Нажмите <strong>«Подключить кассу»</strong>,
              чтобы сгенерировать код.
            </div>
          ) : (
            <div className="p-10 text-center text-sm text-slate-500">
              <Smartphone size={24} className="mx-auto text-slate-300 mb-2" />
              Пока нет подключённых официантов. Нажмите <strong>«Подключить официанта»</strong> —
              покажем код, который официант введёт в приложении.
            </div>
          )
        ) : tab === 'kassa' ? (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3 font-medium">Устройство</th>
                <th className="px-4 py-3 font-medium">Статус</th>
                <th className="px-4 py-3 font-medium">Последняя активность</th>
                <th className="px-4 py-3 font-medium">Очередь</th>
                <th className="px-4 py-3 font-medium">IP</th>
                <th className="px-4 py-3 font-medium">Тип</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.id}
                  className={`border-b border-slate-50 last:border-b-0 ${
                    d.revoked ? 'opacity-60' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{d.name}</div>
                    <div className="text-xs text-slate-400 font-mono">
                      id: {d.id.slice(0, 10)}…
                    </div>
                  </td>
                  <td className="px-4 py-3">{onlineBadge(d)}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {d.lastSeenAt ? formatDate(d.lastSeenAt) : '—'}
                  </td>
                  <td className="px-4 py-3">{pendingBadge(d)}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{d.lastIp ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {shortAgent(d.userAgent)}
                    {d.appVersion && <div className="font-mono text-slate-400">v{d.appVersion}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!d.revoked && (
                      <button
                        className="btn-ghost text-rose-600 hover:bg-rose-50"
                        onClick={() => setRevokeTarget(d)}
                        title="Отозвать доступ"
                      >
                        <Ban size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3 font-medium">Официант</th>
                <th className="px-4 py-3 font-medium">Телефон</th>
                <th className="px-4 py-3 font-medium">Статус</th>
                <th className="px-4 py-3 font-medium">Последняя активность</th>
                <th className="px-4 py-3 font-medium">Версия</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.id}
                  className={`border-b border-slate-50 last:border-b-0 ${
                    d.revoked ? 'opacity-60' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{waiterName(d)}</div>
                    {d.waiter?.email && (
                      <div className="text-xs text-slate-400">{d.waiter.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {d.name}
                    {d.revoked && d.revokedReason && (
                      <div className="text-xs text-slate-400">{d.revokedReason}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">{waiterBadge(d)}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {d.lastSeenAt ? formatDate(d.lastSeenAt) : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">
                    {d.appVersion ? `v${d.appVersion}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!d.revoked && (
                      <button
                        className="btn-ghost text-rose-600 hover:bg-rose-50"
                        onClick={() => setRevokeTarget(d)}
                        title="Отключить телефон"
                      >
                        <Ban size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      {/* Информационный блок — что такое pairing. */}
      {!moduleOff && (
      <div className="mt-6 rounded-lg bg-slate-50 border border-slate-100 p-4 text-sm text-slate-600 space-y-1.5">
        <div className="flex items-center gap-2 font-medium text-slate-800">
          <ShieldAlert size={14} className="text-amber-500" />
          Как это работает
        </div>
        <p>Код действителен 5 минут и работает один раз.</p>
        {tab === 'kassa' ? (
          <>
            <p>После подключения касса запоминает доступ навсегда, пока вы не отзовёте его в этом списке.</p>
            <p>Если планшет потерян — жмите «Отозвать», и эта касса больше не сможет отправлять заказы.</p>
          </>
        ) : (
          <>
            <p>
              Телефон запоминает доступ, пока вы не отключите его здесь или официант не нажмёт
              «Выйти» в приложении.
            </p>
            <p>
              Заблокируйте или удалите официанта в «Пользователях» — его телефон отключится сразу.
            </p>
          </>
        )}
      </div>
      )}

      {moduleDialog === 'enable' && waitersModule && (
        <EnableWaitersModal
          status={waitersModule}
          pending={enableMut.isPending}
          onClose={() => setModuleDialog(null)}
          onConfirm={() => enableMut.mutate()}
        />
      )}

      {moduleDialog === 'disable' && waitersModule && (
        <DisableWaitersModal
          status={waitersModule}
          pending={disableMut.isPending}
          onClose={() => setModuleDialog(null)}
          onConfirm={() => disableMut.mutate()}
        />
      )}

      {pickingWaiter && (
        <WaiterPairModal
          pending={waiterPairMut.isPending}
          onClose={() => setPickingWaiter(false)}
          onSubmit={(target) => waiterPairMut.mutate(target)}
        />
      )}

      {pairCode && (
        <PairCodeModal
          data={pairCode.data}
          variant={pairCode.variant}
          onClose={() => {
            setPairCode(null);
            invalidate();
          }}
          onRegenerate={() => {
            const variant = pairCode.variant;
            setPairCode(null);
            if (variant === 'kassa') pairMut.mutate(undefined);
            else if (waiterTarget) waiterPairMut.mutate(waiterTarget);
          }}
        />
      )}

      {revokeTarget && (
        <Modal
          open
          onClose={() => setRevokeTarget(null)}
          title={revokeTarget.role === 'WAITER' ? 'Отключить телефон' : 'Отозвать доступ'}
        >
          <div className="space-y-4">
            {revokeTarget.role === 'WAITER' ? (
              <p className="text-sm text-slate-600">
                Телефон официанта <strong>{waiterName(revokeTarget)}</strong> больше не сможет
                отправлять заказы. Чтобы вернуть доступ, подключите телефон заново.
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                Устройство <strong>{revokeTarget.name}</strong> больше не сможет принимать
                заказы. Отменить это нельзя — если касса вернётся, подключите её заново.
              </p>
            )}
            <input
              ref={revokeReasonRef}
              className="input"
              placeholder={
                revokeTarget.role === 'WAITER'
                  ? 'Причина (необязательно): потерял телефон, уволился…'
                  : 'Причина (необязательно): украли, потеряли, вышел кассир…'
              }
              maxLength={200}
            />
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setRevokeTarget(null)}>
                Отмена
              </button>
              <button
                className="btn-primary bg-rose-600 hover:bg-rose-700"
                disabled={revokeMut.isPending}
                onClick={() =>
                  revokeMut.mutate({
                    id: revokeTarget.id,
                    reason: revokeReasonRef.current?.value || undefined,
                  })
                }
              >
                {revokeTarget.role === 'WAITER' ? 'Отключить' : 'Отозвать'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
